import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {LibraryDomainUseDTO, LibraryDTO} from "../../../../models/library";
import React from "react";
import {useTranslation} from "react-i18next";
import {API, createApiConfig} from "../../../../utils/API";
import {Box} from "@material-ui/core";
import {DeleteActionButton, InfoActionButton, SwapVerticallyActionButton} from "../../../common/buttons";
import {LoadingBackdrop} from "../../../common/Loading";
import {DomainDialog} from "../../../values/dialogs/DomainDialog";
import {useKeycloak} from "@react-keycloak/web";
import {ConfirmationDialog, InformationDialog} from "../../../common/dialogs";
import {AxiosError} from "axios";
import ItemPositionInLibrary, {DomainPositionInLibrary} from "../util/LibraryItemPosition";
import {LibraryDomainRenderDelete} from "./delete/LibraryDomainRenderDelete";
import {ErrorDeleteDTO, rectifyErrorDelete} from "../../../../models/error";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        domainRow: {
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #F3F4F6",
            padding: `${theme.spacing(1.25)}px ${theme.spacing(2)}px`,
            display: "flex",
            alignItems: "center",
        },
        dragHandle: {
            marginRight: theme.spacing(0.5),
            display: "flex",
            cursor: "move",
            color: "#9CA3AF",
        },
        domainItemName: {
            marginLeft: theme.spacing(0.5),
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "#111827",
            display: "flex",
            alignItems: "center",
            flexGrow: 1,
        },
        infoButton: {},
    })
);

interface LibraryDomainUseRowProps {
    library: LibraryDTO;
    libraryDomainUse: LibraryDomainUseDTO;
    onUpdate: (library: LibraryDTO) => void;
    shiftingItemPosition: ItemPositionInLibrary | null;
    handleShiftStart: (itemPosition: DomainPositionInLibrary) => void;
}

interface LibraryDomainUseRowState {
    name: "info" | "init" | "edit" | "saving" | "delete_confirm" | "delete_inform" | "deleting" | "failed" | "deleted";
    error: ErrorDeleteDTO | null;
}

export const LibraryDomainUseRow: React.FC<LibraryDomainUseRowProps> = (props: LibraryDomainUseRowProps) => {
    //== Init =================================================================
    const classes = useStyles();
    const [t] = useTranslation("libraries");
    const {keycloak, initialized} = useKeycloak();
    const initState: LibraryDomainUseRowState = {
        name: "init",
        error: null,
    };
    const [state, setState] = React.useState(initState);
    //== Effects ==============================================================
    React.useEffect(() => {
        if (state.name === "deleting") {
            API.delete<LibraryDTO>(
                `/domains/library-use/${props.libraryDomainUse.id}`,
                createApiConfig(keycloak, initialized)
            )
                .then((res) => {
                    props.onUpdate(res.data);
                    //setState({...state, name: "deleted"});
                })
                .catch((err: AxiosError) => {
                    if (err.response?.status === 400) {
                        const error: ErrorDeleteDTO = rectifyErrorDelete(err.response.data);
                        setState({...state, name: "delete_inform", error: error});
                    } else {
                        setState({...state, name: "failed"});
                    }
                });
        }
    }, [state, setState, props]);
    //== Handlers =============================================================
    const handleInfoOpen = () => {
        setState({...state, name: "info"});
    };
    const handleDelete = () => {
        setState({...state, name: "delete_confirm"});
    };
    const handleDeleteConfirm = () => {
        setState({...state, name: "deleting"});
    };
    const handleReset = () => {
        setState({...state, name: "init"});
    };
    const isShiftingOtherThanThisOne =
        props.shiftingItemPosition &&
        (props.shiftingItemPosition.domainIndex !== props.libraryDomainUse.orderNumber ||
            props.shiftingItemPosition.goalIndex !== null);
    //== Render ===============================================================
    return (
        <Box className={classes.domainRow}>
            <div className={classes.dragHandle} style={isShiftingOtherThanThisOne ? {visibility: "hidden"} : {}}>
                <SwapVerticallyActionButton
                    onClick={() =>
                        props.handleShiftStart({
                            domainIndex: props.libraryDomainUse.orderNumber,
                            goalIndex: null,
                            indicatorIndex: null,
                        })
                    }
                />
            </div>
            <Box className={classes.domainItemName}>{props.libraryDomainUse.domain.name}</Box>
            <Box className={classes.infoButton}>
                <InfoActionButton onClick={handleInfoOpen} />
            </Box>
            <Box>
                <DeleteActionButton onClick={handleDelete} />
            </Box>
            <DomainDialog
                domain={props.libraryDomainUse.domain}
                handleClose={handleReset}
                open={state.name === "info"}
            />
            <ConfirmationDialog
                id={`delete-confirm-dialog-${props.libraryDomainUse.id}`}
                title={t("domain.confirm_delete_use.title")}
                text={t("domain.confirm_delete_use.text", {title: props.libraryDomainUse.domain.name})}
                open={state.name === "delete_confirm"}
                onAgree={handleDeleteConfirm}
                onDisagree={handleReset}
            />
            {state.error !== null && (
                <InformationDialog
                    id={`delete-inform-dialog-${props.libraryDomainUse.id}`}
                    title={t(`domain.confirm_delete.inform.title`)}
                    text={t(`domain.confirm_delete.inform.text`, {
                        title: props.libraryDomainUse.domain.name ?? "",
                        count: state.error?.relatedEntities.length ?? 0,
                    })}
                    open={state.name === "delete_inform"}
                    onClose={handleReset}
                >
                    <LibraryDomainRenderDelete
                        entity={{name: props.libraryDomainUse.domain.name, id: props.libraryDomainUse.id}}
                        error={state.error}
                    />
                </InformationDialog>
            )}
            {state.name.endsWith("ing") && <LoadingBackdrop />}
        </Box>
    );
};
